import type { FocusEvent, InputHTMLAttributes, MouseEventHandler } from 'react'

import { useFocusRing } from '@react-aria/focus'
import { useHover } from '@react-aria/interactions'

import type { StylableElementProps } from '@subaio/components'

import { InputComponent } from './InputComponent'
import { InputContainer } from './InputContainer'

type Props = StylableElementProps & {
	hasError: boolean
	isDisabled: boolean
	isReadOnly: boolean
	shouldAutofocus: boolean
	inputProps: InputHTMLAttributes<HTMLInputElement>
	onClick: MouseEventHandler<HTMLDivElement>
}

export const Field = ({
	hasError,
	isDisabled,
	isReadOnly,
	shouldAutofocus,
	inputProps,
	onClick,
	style,
	className,
}: Props) => {
	const { hoverProps, isHovered } = useHover({})
	const { isFocusVisible, focusProps, isFocused } = useFocusRing({ isTextInput: true, autoFocus: shouldAutofocus })

	return (
		<InputContainer
			{...hoverProps}
			hasError={hasError}
			isDisabled={isDisabled}
			isHovered={isHovered}
			isFocusVisible={isFocusVisible}
			isFocus={isFocused}
			isReadOnly={isReadOnly}
			style={style}
			className={className}
			onClick={onClick}
		>
			<InputComponent
				{...inputProps}
				{...focusProps}
				disabled={isDisabled}
				readOnly={isReadOnly}
				hasError={hasError}
				onFocus={(event: FocusEvent<HTMLInputElement>) => {
					focusProps.onFocus?.(event)
				}}
				onBlur={(event: FocusEvent<HTMLInputElement>) => {
					focusProps.onBlur?.(event)
					inputProps.onBlur?.(event)
				}}
			/>
		</InputContainer>
	)
}
